import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { MapPin, Mail, Phone } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { HeroSection } from '@/components/sections/HeroSection'

interface FormData {
  name: string
  surname: string
  email: string
  message: string
  privacy: boolean
}

interface FormErrors {
  name?: string
  surname?: string
  email?: string
  message?: string
  privacy?: string
}

export function ContactPage() {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    surname: '',
    email: '',
    message: '',
    privacy: false,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitted, setSubmitted] = useState(false)

  const validate = (): boolean => {
    const newErrors: FormErrors = {}
    if (!formData.name.trim()) newErrors.name = t('contact.errorRequired')
    if (!formData.surname.trim()) newErrors.surname = t('contact.errorRequired')
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errorRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errorEmail')
    }
    if (!formData.message.trim()) newErrors.message = t('contact.errorRequired')
    if (!formData.privacy) newErrors.privacy = t('contact.errorPrivacy')
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      setSubmitted(true)
    }
  }

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <>
      <SEOHead titleKey="seo.contacts.title" descriptionKey="seo.contacts.description" />

      <HeroSection
        title={t('contact.heroTitle')}
        subtitle={t('contact.heroSubtitle')}
        backgroundImage="/media/about/sede-selea.jpg"
        compact
      />

      <section className="section-padding">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6">{t('contact.infoTitle')}</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.addressLabel')}</h3>
                    <p className="text-text-muted text-sm">
                      Selea s.r.l.<br />
                      Via Aldo Moro 69<br />
                      Cicognara (MN), Italy
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Email</h3>
                    <p className="text-text-muted text-sm">info@selea.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.phoneLabel')}</h3>
                    <p className="text-text-muted text-sm">+39 0375 97360</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="card p-8 text-center"
                >
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-success" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{t('contact.successTitle')}</h3>
                  <p className="text-text-muted">{t('contact.successDesc')}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="card p-8" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        {t('contact.nameLabel')} *
                      </label>
                      <input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                          errors.name ? 'border-accent' : 'border-surface-border'
                        }`}
                        placeholder={t('contact.namePlaceholder')}
                      />
                      {errors.name && <p className="text-accent text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="surname" className="block text-sm font-medium mb-2">
                        {t('contact.surnameLabel')} *
                      </label>
                      <input
                        id="surname"
                        type="text"
                        required
                        value={formData.surname}
                        onChange={(e) => handleChange('surname', e.target.value)}
                        className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                          errors.surname ? 'border-accent' : 'border-surface-border'
                        }`}
                        placeholder={t('contact.surnamePlaceholder')}
                      />
                      {errors.surname && <p className="text-accent text-xs mt-1">{errors.surname}</p>}
                    </div>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                        errors.email ? 'border-accent' : 'border-surface-border'
                      }`}
                      placeholder="email@example.com"
                    />
                    {errors.email && <p className="text-accent text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      {t('contact.messageLabel')} *
                    </label>
                    <textarea
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-vertical ${
                        errors.message ? 'border-accent' : 'border-surface-border'
                      }`}
                      placeholder={t('contact.messagePlaceholder')}
                    />
                    {errors.message && <p className="text-accent text-xs mt-1">{errors.message}</p>}
                  </div>
                  <div className="mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        checked={formData.privacy}
                        onChange={(e) => handleChange('privacy', e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-surface-border text-accent focus:ring-accent"
                      />
                      <span className="text-sm text-text-muted">
                        {t('contact.privacyConsent')} *
                      </span>
                    </label>
                    {errors.privacy && <p className="text-accent text-xs mt-1">{errors.privacy}</p>}
                  </div>
                  <button type="submit" className="btn-primary w-full sm:w-auto">
                    {t('buttons.send')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
